import { Observable, of, merge } from 'rxjs';
import { withLatestFrom, switchMap, catchError, map, tap, distinctUntilKeyChanged } from 'rxjs/operators';

import { fireGTMEvent } from 'src/features/analytics/hooks/useGtm';

import {
    HubspotAnalyticEvents,
    HubspotEvents,
    HubspotProduct,
    HubspotStartedCheckoutGtmEvent,
    HubspotTagManagerEvents,
} from '../../types';

import { loggedInUsersEpic } from './logged-in-users.epic';
// import { guestHomeDeliveryEpic } from './guest-home-delivery.epic';
// import { guestCollectionEpic } from './guest-collection.epic';

/**
 * Listens for the user starting the checkout process.
 *
 * The details of how this is triggered depends on the user journey:
 * 1. Logged in users
 *     - The user navigates to the checkout page and their account details
 *       are used to trigger the event.
 * 2. Guests using home delivery
 *    - The user sets their shipping address and proceeds to the payment step.
 * 3. Guests using collection use the `Checkout.SetPendingPayment` event.
 *   - The user sets their billing address and proceeds with payment.
 */
export function hubspotStartCheckout(action$: Observable<any>, state$: Observable<any>): Observable<any> {
    const loggedInTrigger$ = loggedInUsersEpic(action$, state$);

    // Leaving these commented in case Woodies want to add them in
    // They need adding into the `merge` brackets below
    // const guestHomeDelivery$ = guestHomeDeliveryEpic(action$, state$);
    // const guestCollection$ = guestCollectionEpic(action$, state$);

    // Listern for each of the Checkout user journeys
    return merge(loggedInTrigger$).pipe(
        withLatestFrom(state$),

        // Check the event against the Cart ID
        map(([customerInfo, state]) => ({
            ...(customerInfo ?? {}),
            cartId: state.cart?.cartId,
        })),

        // Only trigger the Hubspot event once per Cart ID.
        distinctUntilKeyChanged('cartId'),

        withLatestFrom(state$),

        // Prepare the GTM event payload
        switchMap(([customerInfo, state]) => {
            const cart_id = state.cart?.cartId ?? undefined;
            const baseUrl = window?.location?.origin ?? '';
            const checkout_url = baseUrl + state.router.current?.urlKey;
            const cart_url = baseUrl + '/basket';

            const cartItems: HubspotProduct[] = state.cart?.items?.map((cartItem) => {
                const item_id = cartItem?.id;
                const extraProductInformation = state.products?.byIds?.[item_id];

                const sale_price = extraProductInformation?.price?.special_price ?? undefined;
                const maxPrice = extraProductInformation?.price?.maximum_price ?? cartItem?.prices?.price ?? undefined;

                let price = maxPrice;
                let was_price;

                if (sale_price) {
                    price = sale_price;
                    was_price = maxPrice;
                }

                let product_url = cartItem?.product.pathname ?? undefined;

                if (product_url && !product_url?.startsWith('http')) {
                    product_url = baseUrl + '/' + product_url;
                }

                let product_image_url = cartItem?.product?.small_image?.url ?? undefined;

                if (product_image_url && !product_image_url.startsWith('http')) {
                    product_image_url = baseUrl + product_image_url;
                }

                const labels =
                    extraProductInformation?.labels
                        ?.map((label) => label.name)
                        .filter((label): label is string => label !== null && label !== undefined) ?? [];

                return {
                    currency: state.cart?.raw?.prices?.grand_total?.currency ?? 'EUR',
                    item_id,
                    labels,
                    name: cartItem?.product?.name,
                    price,
                    product_image_url,
                    product_url,
                    quantity: cartItem?.quantity,
                    sale_price,
                    save_price: sale_price,
                    sku: cartItem?.product?.sku,
                    was_price,
                };
            });

            const gtmEvent: HubspotStartedCheckoutGtmEvent = {
                cart_id,
                cart: {
                    cart_id,
                    cart_url,
                    delivery_type: state.cart?.raw?.woodies?.basket_type,
                    items_count: state.cart?.items?.length ?? 0,
                    items_qty: state.cart?.items_qty ?? 0,
                    total_value: state.cart?.totals?.grand_total ?? 0,
                },
                checkout_id: cart_id,
                checkout_url,
                customer_email: customerInfo.email,
                event_name: HubspotAnalyticEvents.STARTED_CHECKOUT,
                event: HubspotTagManagerEvents.STARTED_CHECKOUT,
                firstname: customerInfo.firstName,
                is_guest: customerInfo.isGuest,
                items: cartItems,
                lastname: customerInfo.lastName,
                session_id: cart_id,
                timestamp: new Date().toISOString(),
            };

            return of(gtmEvent);
        }),

        // Trigger the GTM event
        tap((gtmEvent) => fireGTMEvent(gtmEvent)),

        // Send the event to Redux
        map((gtmEvent) => ({
            type: HubspotEvents.STARTED_CHECKOUT,
            data: gtmEvent,
        })),

        catchError((error) => {
            return of({
                type: HubspotEvents.STARTED_CHECKOUT_ERROR,
                payload: error,
            });
        }),
    );
}
